// Deterministic comparison helpers — every sentence is built only from stored
// tool fields (no-hallucination rule), used by /vs and /alternatives pages.

export const fmtList = (v) => (Array.isArray(v) && v.length ? v.join(", ") : "");

export function tiers(t) {
  const pd = (t?.pricing_detail || []).filter((p) => p && (p.plan || p.price));
  return pd.slice(0, 5).map((p) => [p.plan, p.price].filter(Boolean).join(" "));
}

export const tiersText = (t) => tiers(t).join(" · ");

export function hasFreeTier(t) {
  const p = String(t?.pricing || "").toUpperCase();
  if (p === "FREE" || p.startsWith("FREE")) return true;
  return (t?.pricing_detail || []).some(
    (x) => /^\s*(\$0|0|free)\b/i.test(String(x?.price || "")) || /free/i.test(String(x?.plan || "")),
  );
}

export function firstPaidTier(t) {
  return tiers(t).find((s) => !/(\$0\b|free)/i.test(s)) || null;
}

const diff = (a, b) => (a || []).filter((x) => !(b || []).includes(x));

export function keyDifferences(a, b) {
  const out = [];
  const fa = hasFreeTier(a);
  const fb = hasFreeTier(b);
  if (fa && !fb) out.push(`${a.title} has a free tier; ${b.title} is ${b.pricing || "paid"}${firstPaidTier(b) ? ` (from ${firstPaidTier(b)})` : ""}.`);
  if (fb && !fa) out.push(`${b.title} has a free tier; ${a.title} is ${a.pricing || "paid"}${firstPaidTier(a) ? ` (from ${firstPaidTier(a)})` : ""}.`);
  if (a.asset_class && b.asset_class && a.asset_class !== b.asset_class)
    out.push(`${a.title} covers ${a.asset_class}; ${b.title} covers ${b.asset_class}.`);
  if (a.is_open_source && !b.is_open_source) out.push(`${a.title} is open source; ${b.title} is not.`);
  if (b.is_open_source && !a.is_open_source) out.push(`${b.title} is open source; ${a.title} is not.`);
  const da = diff(a.delivery, b.delivery).filter((d) => ["api", "cli", "desktop", "mobile", "self_host", "mcp"].includes(d));
  const db = diff(b.delivery, a.delivery).filter((d) => ["api", "cli", "desktop", "mobile", "self_host", "mcp"].includes(d));
  if (da.length) out.push(`Only ${a.title} is available via ${da.join(", ").replace(/_/g, " ")}.`);
  if (db.length) out.push(`Only ${b.title} is available via ${db.join(", ").replace(/_/g, " ")}.`);
  const wa = diff(a.workflow_position, b.workflow_position);
  const wb = diff(b.workflow_position, a.workflow_position);
  if (wa.length) out.push(`${a.title} also covers ${wa.join(", ").toLowerCase()} in the workflow.`);
  if (wb.length) out.push(`${b.title} also covers ${wb.join(", ").toLowerCase()} in the workflow.`);
  const ua = diff(a.user_base, b.user_base);
  const ub = diff(b.user_base, a.user_base);
  if (ua.length) out.push(`${a.title} lists ${ua.join(", ")} users; ${b.title} does not.`);
  if (ub.length) out.push(`${b.title} lists ${ub.join(", ")} users; ${a.title} does not.`);
  const sa = diff(a.strategies, b.strategies);
  const sb = diff(b.strategies, a.strategies);
  if (sa.length) out.push(`${a.title} is tagged for ${sa.join(", ").toLowerCase()}.`);
  if (sb.length) out.push(`${b.title} is tagged for ${sb.join(", ").toLowerCase()}.`);
  return out.slice(0, 8);
}

export function softwareAppLd(t, siteUrl) {
  const price = String(t.pricing || "").toUpperCase();
  const offers = {
    "@type": "Offer",
    priceCurrency: "USD",
    category: price === "FREE" ? "Free" : price.includes("CALL") ? "Contact for pricing" : "Paid",
  };
  if (hasFreeTier(t)) offers.price = "0";
  return {
    "@type": "SoftwareApplication",
    name: t.title,
    url: siteUrl(`/tool/${t.slug}`),
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    description: t.key_feature || t.description,
    offers,
  };
}
